import React, { useState } from "react";
import CTA from "@/components/atoms/CTA";
import Dropzone from "@/components/molecules/Dropzone";
import Modal, { ModalCTAs } from "@/components/molecules/Modal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fileToBase64Raw } from "@/utils/FileUtils";
import { WeightReportUploadRequest } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  UploadWeightReportFormInput,
  UploadWeightReportSchema,
} from "@/components/organisms/UploadWeightReportModal/validation/validationSchema";
import { uploadWeightReport } from "@/services/WeightReports";
import { notifications } from "@mantine/notifications";
import DocumentTypeEnum from "@/constants/enums/DocumentTypeEnum";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

interface UploadWeightReportModal {
  open: boolean;
  setOpen: any;
}

export const UploadWeightReportModal: React.FC<UploadWeightReportModal> = ({ open, setOpen }) => {
  const [files, setFiles] = useState<File[]>([]);
  // const [ uploadedReportId, setUploadedReportId ] = useState<number | null>(null);
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();

  const { handleSubmit, reset } = useForm<UploadWeightReportFormInput>({
    resolver: zodResolver(UploadWeightReportSchema),
    values: {
      document: "",
      customerId: 0,
      senderCompanyId: currentCompany?.companyId as number,
      externalId: null,
      fileType: "PDF",
      documentType: "WEIGHT_REPORT",
      documentTitle: "",
      languageId: 0,
      tiltedScan: true,
    },
  });

  const { mutate: uploadWeightReportMutation, isPending: isPendingUpload } = useMutation({
    mutationKey: ["uploadWeightReport"],
    mutationFn: async (req: WeightReportUploadRequest) => {
      return await uploadWeightReport(req);
    },
    onSuccess: async () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.createSuccess"),
          t("notifications:weightReports.weightReportUploaded"),
          "weight-report-uploaded-success"
        )
      );
      await queryClient.invalidateQueries({
        queryKey: ["weightReportLines"],
      });
      reset();
      setFiles([]);
      setOpen(false);
    },
    onError: (_) => {
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.createError"),
          t("notifications:weightReports.weightReportUploadError"),
          "weight-report-uploaded-error"
        )
      );
      reset();
      setFiles([]);
      setOpen(false);
    },
  });

  const onSubmit = async (data: UploadWeightReportFormInput) => {
    if (files.length === 0) {
      // Handle error - no file selected
      return;
    }
    const base64 = await fileToBase64Raw(files[0]);
    const translatedRequest: WeightReportUploadRequest = {
      ...data,
      customerId: null,
      document: base64,
      senderCompanyId: currentCompany?.companyId as number,
      fileType: "PDF",
      externalId: null,
      documentType: DocumentTypeEnum.WEIGHT_REPORT,
      documentTitle: files[0].name,
      tiltedScan: false,
    };
    uploadWeightReportMutation(translatedRequest);
  };

  return (
    <Modal opened={open} onClose={setOpen} title={t("weightReports:modalTitle")} size={"lg"}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid p={20}>
          <Grid.Col span={12}>
            <Dropzone
              type={"dropzone"}
              multiple={false}
              showAsImageCarousel={false}
              origin={"local"}
              files={files}
              setFiles={setFiles}
            />
          </Grid.Col>
        </Grid>
        <ModalCTAs>
          <CTA
            intent={"secondary"}
            type={"reset"}
            disabled={isPendingUpload}
            onClick={() => {
              reset();
              setOpen(false);
            }}
          >
            {t("actions:cancel")}
          </CTA>
          <CTA intent={"primary"} type={"submit"} disabled={isPendingUpload} loading={isPendingUpload}>
            {t("actions:upload")}
          </CTA>
        </ModalCTAs>
      </form>
    </Modal>
  );
};
